import { logLegacyGenericEvent } from "../../native-modules/tare/logLegacyGenericEvent";
import { logScreenViewWithExtraParams } from "../../native-modules/tare/logScreenViewWithExtraParams";
import { logTapButtonWithExtraParams } from "../../native-modules/tare/logTapButtonWithExtraParams";
import type { GhostwriterButtonAction } from "../context/useGhostwriterForViewJob";
import { type ScheduleApplyStatus, ScheduleApplyStatuses } from "../models/ScheduleApplyJob";
import {
  type GhostwriterFormStep,
  type GhostwriterAutoApplyCta,
  ELEMENT_NAME,
  EVENT_NAME,
  EVENT_PARAMETER,
  EVENT_TAG,
  MY_JOBS_SURFACE_TAGS,
  MYJOBS_AUTO_APPLY_CTA_ELEMENT_NAME,
  MYJOBS_AUTO_APPLY_SUB_TAB,
  SCREEN_NAME,
} from "./constants";

const DEFAULT_TAGS = [EVENT_TAG.GHOSTWRITER];

/** Primary CTA on a Ghostwriter Auto Apply job card in My Jobs; `elementName` is the button label. */
export function logGhostwriterAutoApplyCtaTap(jobKey: string, cta: GhostwriterAutoApplyCta): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.RN_MYJOBS,
    MYJOBS_AUTO_APPLY_CTA_ELEMENT_NAME[cta],
    MY_JOBS_SURFACE_TAGS,
    {
      [EVENT_PARAMETER.MYJOBS_SUB_TAB]: MYJOBS_AUTO_APPLY_SUB_TAB,
      [EVENT_PARAMETER.JOB_KEY]: jobKey,
      [EVENT_PARAMETER.VALUE]: cta,
    },
  );
}

/** Overflow (more) menu on a Ghostwriter Auto Apply job card → manage job. */
export function logGhostwriterAutoApplyOverflowMenuTap(jobKey: string): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.RN_MYJOBS,
    ELEMENT_NAME.MYJOBS_AUTO_APPLY_OVERFLOW_MENU,
    MY_JOBS_SURFACE_TAGS,
    {
      [EVENT_PARAMETER.MYJOBS_SUB_TAB]: MYJOBS_AUTO_APPLY_SUB_TAB,
      [EVENT_PARAMETER.JOB_KEY]: jobKey,
    },
  );
}

export type GhostwriterAutoApplyStopDialogAction = "confirm" | "cancel";

/** Stop auto apply confirmation dialog actions. */
export function logGhostwriterAutoApplyStopDialogTap(
  jobKey: string,
  action: GhostwriterAutoApplyStopDialogAction,
): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.RN_MYJOBS,
    ELEMENT_NAME.MYJOBS_AUTO_APPLY_STOP_DIALOG,
    MY_JOBS_SURFACE_TAGS,
    {
      [EVENT_PARAMETER.MYJOBS_SUB_TAB]: MYJOBS_AUTO_APPLY_SUB_TAB,
      [EVENT_PARAMETER.JOB_KEY]: jobKey,
      [EVENT_PARAMETER.VALUE]: action,
    },
  );
}

/** Manage this job sheet: menu row (`action` = item id) or sheet dismissed (`close`). */
export function logGhostwriterManageJobMenuTap(jobKey: string, action: string): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.RN_MYJOBS,
    ELEMENT_NAME.MYJOBS_AUTO_APPLY_MANAGE_JOB_MENU,
    MY_JOBS_SURFACE_TAGS,
    {
      [EVENT_PARAMETER.MYJOBS_SUB_TAB]: MYJOBS_AUTO_APPLY_SUB_TAB,
      [EVENT_PARAMETER.JOB_KEY]: jobKey,
      [EVENT_PARAMETER.VALUE]: action,
    },
  );
}

export type GhostwriterUndoManageJobDialogAction = "confirm" | "cancel";

/** Undo auto apply confirmation alert from manage job sheet. */
export function logGhostwriterUndoManageJobDialogTap(
  jobKey: string,
  action: GhostwriterUndoManageJobDialogAction,
): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.RN_MYJOBS,
    ELEMENT_NAME.MYJOBS_AUTO_APPLY_UNDO_MANAGE_JOB_DIALOG,
    MY_JOBS_SURFACE_TAGS,
    {
      [EVENT_PARAMETER.MYJOBS_SUB_TAB]: MYJOBS_AUTO_APPLY_SUB_TAB,
      [EVENT_PARAMETER.JOB_KEY]: jobKey,
      [EVENT_PARAMETER.VALUE]: action,
    },
  );
}

export function logGhostwriterEntrypointImpression(jobKey: string): void {
  void logScreenViewWithExtraParams(SCREEN_NAME.RN_VIEW_JOB, DEFAULT_TAGS, {
    [EVENT_PARAMETER.JOB_KEY]: jobKey,
  });
}

export function logGhostwriterEntrypointTapFirstTime(jobKey: string): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.RN_VIEW_JOB,
    ELEMENT_NAME.ENTRYPOINT_FIRST_TIME,
    DEFAULT_TAGS,
    { [EVENT_PARAMETER.JOB_KEY]: jobKey },
  );
}

export function logGhostwriterEntrypointTapReturning(jobKey: string): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.RN_VIEW_JOB,
    ELEMENT_NAME.ENTRYPOINT_RETURNING,
    DEFAULT_TAGS,
    { [EVENT_PARAMETER.JOB_KEY]: jobKey },
  );
}

export function logGhostwriterConsentImpression(jobKey: string): void {
  void logScreenViewWithExtraParams(SCREEN_NAME.GHOSTWRITER_ONBOARDING, DEFAULT_TAGS, {
    [EVENT_PARAMETER.JOB_KEY]: jobKey,
  });
}

export function logGhostwriterConsentContinue(jobKey: string): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.GHOSTWRITER_ONBOARDING,
    ELEMENT_NAME.CONSENT_CONTINUE,
    DEFAULT_TAGS,
    { [EVENT_PARAMETER.JOB_KEY]: jobKey },
  );
}

export function logGhostwriterConsentDismiss(jobKey: string): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.GHOSTWRITER_ONBOARDING,
    ELEMENT_NAME.CONSENT_DISMISS,
    DEFAULT_TAGS,
    { [EVENT_PARAMETER.JOB_KEY]: jobKey },
  );
}

export function logGhostwriterPushPrimerImpression(jobKey: string): void {
  void logScreenViewWithExtraParams(SCREEN_NAME.GHOSTWRITER_PUSH_PRIMER, DEFAULT_TAGS, {
    [EVENT_PARAMETER.JOB_KEY]: jobKey,
  });
}

export function logGhostwriterPushPrimerAllow(jobKey: string, permissionStatus: string): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.GHOSTWRITER_PUSH_PRIMER,
    ELEMENT_NAME.PUSH_PRIMER_ALLOW,
    DEFAULT_TAGS,
    { [EVENT_PARAMETER.JOB_KEY]: jobKey, [EVENT_PARAMETER.VALUE]: permissionStatus },
  );
}

export function logGhostwriterPushPrimerDeny(jobKey: string): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.GHOSTWRITER_PUSH_PRIMER,
    ELEMENT_NAME.PUSH_PRIMER_DENY,
    DEFAULT_TAGS,
    { [EVENT_PARAMETER.JOB_KEY]: jobKey },
  );
}

export function logGhostwriterPrefillingStageImpression(jobKey: string, stage: string): void {
  void logScreenViewWithExtraParams(SCREEN_NAME.GHOSTWRITER_PREFILLING, DEFAULT_TAGS, {
    [EVENT_PARAMETER.JOB_KEY]: jobKey,
    [EVENT_PARAMETER.STAGE]: stage,
  });
}

export function logGhostwriterPrefillingClose(
  jobKey: string,
  stage: string,
  durationMs: number,
): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.GHOSTWRITER_PREFILLING,
    ELEMENT_NAME.PREFILLING_CLOSE,
    DEFAULT_TAGS,
    {
      [EVENT_PARAMETER.JOB_KEY]: jobKey,
      [EVENT_PARAMETER.STAGE]: stage,
      [EVENT_PARAMETER.DURATION_MS]: durationMs,
    },
  );
}

export function logGhostwriterPrefillingLeaveAndNotify(
  jobKey: string,
  stage: string,
  durationMs: number,
): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.GHOSTWRITER_PREFILLING,
    ELEMENT_NAME.PREFILLING_LEAVE_AND_NOTIFY,
    DEFAULT_TAGS,
    {
      [EVENT_PARAMETER.JOB_KEY]: jobKey,
      [EVENT_PARAMETER.STAGE]: stage,
      [EVENT_PARAMETER.DURATION_MS]: durationMs,
    },
  );
}

export function logGhostwriterPrefillingComplete(jobKey: string, durationMs: number): void {
  void logScreenViewWithExtraParams(SCREEN_NAME.GHOSTWRITER_PREFILLING, DEFAULT_TAGS, {
    [EVENT_PARAMETER.JOB_KEY]: jobKey,
    [EVENT_PARAMETER.DURATION_MS]: durationMs,
  });
}

export function logGhostwriterReviewImpression(jobKey: string, source: string): void {
  void logScreenViewWithExtraParams(SCREEN_NAME.GHOSTWRITER_REVIEW, DEFAULT_TAGS, {
    [EVENT_PARAMETER.JOB_KEY]: jobKey,
    [EVENT_PARAMETER.SOURCE]: source,
  });
}

export function logGhostwriterReviewSaveAndClose(jobKey: string): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.GHOSTWRITER_REVIEW,
    ELEMENT_NAME.REVIEW_SAVE_AND_CLOSE,
    DEFAULT_TAGS,
    { [EVENT_PARAMETER.JOB_KEY]: jobKey },
  );
}

export function logGhostwriterReviewSubmit(jobKey: string): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.GHOSTWRITER_REVIEW,
    ELEMENT_NAME.REVIEW_SUBMIT,
    DEFAULT_TAGS,
    { [EVENT_PARAMETER.JOB_KEY]: jobKey },
  );
}

export function logGhostwriterReviewBack(jobKey: string): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.GHOSTWRITER_REVIEW,
    ELEMENT_NAME.REVIEW_BACK,
    DEFAULT_TAGS,
    { [EVENT_PARAMETER.JOB_KEY]: jobKey },
  );
}

export function logGhostwriterReviewClose(jobKey: string): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.GHOSTWRITER_REVIEW,
    ELEMENT_NAME.REVIEW_CLOSE,
    DEFAULT_TAGS,
    { [EVENT_PARAMETER.JOB_KEY]: jobKey },
  );
}

/**
 * RN-8455: the ZIA Auto apply CTA on a third-party job invoked the shared
 * third-party apply-intent capture. Distinct from `ghostwriterEntrypointReturning`
 * (the primary CTA tap) — this marks the capture milestone specifically.
 */
export function logGhostwriterZiaThirdPartyApplyCaptured(jobKey: string): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.RN_VIEW_JOB,
    ELEMENT_NAME.ZIA_THIRD_PARTY_APPLY_CAPTURED,
    DEFAULT_TAGS,
    { [EVENT_PARAMETER.JOB_KEY]: jobKey },
  );
}

export function logGhostwriterThirdPartyApplyStart(
  jobKey: string,
  buttonAction: GhostwriterButtonAction,
): void {
  void logTapButtonWithExtraParams(
    SCREEN_NAME.RN_VIEW_JOB,
    ELEMENT_NAME.ENTRYPOINT_THIRD_PARTY_APPLY,
    DEFAULT_TAGS,
    {
      [EVENT_PARAMETER.JOB_KEY]: jobKey,
      [EVENT_PARAMETER.GHOSTWRITER_STATE]: buttonAction,
    },
  );
}

/** Coarse, low-cardinality category for a Ghostwriter submission failure. */
export type GhostwriterSubmissionErrorReason =
  | "submissionFailed"
  | "userError"
  | "requestError"
  | "unknown";

/** Structural check (by `name`) to avoid importing the GraphQL-heavy `ghostwriterApi`. */
function isSubmitApplicationUserError(
  err: unknown,
): err is Error & { applicationStatus?: ScheduleApplyStatus } {
  return err instanceof Error && err.name === "SubmitApplicationUserError";
}

/** Maps a thrown submission error to a stable, PII-free `errorReason`. */
export function classifyGhostwriterSubmissionError(err: unknown): GhostwriterSubmissionErrorReason {
  if (isSubmitApplicationUserError(err)) {
    return err.applicationStatus === ScheduleApplyStatuses.SubmissionFailed
      ? "submissionFailed"
      : "userError";
  }
  if (err instanceof Error) {
    return "requestError";
  }
  return "unknown";
}

/** Logs a single form submission failure with its session attempt count. */
export function logGhostwriterFormSubmissionError(
  jobKey: string,
  formStep: GhostwriterFormStep,
  errorReason: GhostwriterSubmissionErrorReason,
  attemptCount: number,
): void {
  void logLegacyGenericEvent(EVENT_NAME.FORM_SUBMISSION_ERROR, "error", DEFAULT_TAGS, {
    [EVENT_PARAMETER.JOB_KEY]: jobKey,
    [EVENT_PARAMETER.FORM_STEP]: formStep,
    [EVENT_PARAMETER.ERROR_REASON]: errorReason,
    [EVENT_PARAMETER.ATTEMPT_COUNT]: attemptCount,
  });
}

/** Logs that the same form has failed submission enough times to look stuck. */
export function logGhostwriterFormStuckState(
  jobKey: string,
  formStep: GhostwriterFormStep,
  attemptCount: number,
): void {
  void logLegacyGenericEvent(EVENT_NAME.FORM_STUCK_STATE, "error", DEFAULT_TAGS, {
    [EVENT_PARAMETER.JOB_KEY]: jobKey,
    [EVENT_PARAMETER.FORM_STEP]: formStep,
    [EVENT_PARAMETER.ATTEMPT_COUNT]: attemptCount,
  });
}
